import axios from "axios";
import util from "./util";
import AesUtil from "./aesUtil";

import {ElMessage} from 'element-plus';
import router from '../router';
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// 请求超时时间
axios.defaults.timeout = 1800000;
let cancel = null;
let progressInt = 0;
/**
 * 是否正在跳登录。
 *
 * 打开页面时会并发好几个请求（页面自己的 + 布局/菜单/用户信息等），
 * 会话一过期它们会同时返回 3000 —— 每个都进一次登录分支。
 * 不拦的话，后到的那次会看到商户id已被上一次 clearSession 清掉，
 * 于是走 saas 分支又跳一次，把前面那次自建应用的跳转覆盖掉；
 * 它还会顺手清掉刚写好的回跳地址，登录后人被丢回工作台而不是原页面。
 *
 * 有任意请求成功（2000）就复位 —— 那说明登录态已经恢复，下次过期仍要能正常跳。
 */
let loginRedirecting = false;
function startProgress() {
    // 0.1秒以内不显示进度条
    progressInt ++;
    setTimeout(() => {
        if (progressInt > 0) {
            NProgress.start();
        }
    }, 100);

}
function stopProgress() {
    progressInt --;
    NProgress.done();
}
// axios请求拦截器
axios.interceptors.request.use(
    config => {
        let header = {};
        startProgress();
        if (util.findSessionHeader()) {
            header = util.findSessionHeader();
        } else {
            header = util.findCookieHeader();
        }
        if (header.token) {
            config.headers.token = header.token;
            config.headers.systemCode = header.systemCode;
            config.headers.merchantUuid = header.merchantUuid;
        }
        if (header.grantedToken) {
            config.headers.grantedToken = header.grantedToken;
        }
        // 添加语言
        config.headers["Accept-Language"] = navigator.language || navigator.userLanguage;
        config.cancelToken = new axios.CancelToken(function (c) {
            cancel = c;
        });
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

/**
 * 查商户的企业微信授权信息（拼授权链接用）。
 *
 * ★ 用独立的实例发这个请求，不能复用全局那个 axios：
 * 全局实例上挂着本文件注册的响应拦截器，而这次请求正是被 3000 分支触发的，
 * 再走一遍拦截器就是自己调自己。独立实例没有任何拦截器，也就没有这个递归。
 */
const qywxAuthHttp = axios.create({
    baseURL: process.env.VUE_APP_BASE_URL,
    timeout: 10000,
});

async function fetchQywxAuthInfo(merchantId) {
    const resp = await qywxAuthHttp.get("/wx/base/qywx/app/findAuthInfo", {params: {merchantId}});
    // 网关统一封装：{code, message, data}
    const envelope = resp && resp.data;
    if (!envelope || envelope.code !== "2000") {
        return null;
    }
    return envelope.data;
}

/**
 * 跳转企业微信网页授权，回调到 QywxSelfLogin 换 code 登录。
 *
 * 商户id 放在 state 里透传（回调会原样带回）：挂在 redirect_uri 上会被
 * encodeURIComponent 双重编码，state 是 OAuth 专为透传业务参数准备的通道。
 */
async function redirectToQywxAuth(merchantId) {
    const info = await fetchQywxAuthInfo(merchantId);
    if (!info || !info.qywxCorpId) {
        throw new Error("未取到企业微信授权信息");
    }
    const redirectUri = window.location.origin + "/qywx/self/login";
    let url = "https://open.weixin.qq.com/connect/oauth2/authorize"
        + "?appid=" + info.qywxCorpId
        + "&redirect_uri=" + encodeURIComponent(redirectUri)
        + "&response_type=code&scope=snsapi_privateinfo";
    if (info.qywxAgentId) {
        // 自建应用必须带 agentid，否则企业微信不认这个应用
        url += "&agentid=" + info.qywxAgentId;
    }
    url += "&state=" + merchantId + "#wechat_redirect";
    window.location = url;
}

/**
 * 自动登录专用请求实例
 *
 * ★ 不能复用全局 axios：全局实例挂着本文件的响应拦截器，
 *   自动登录请求再走一遍拦截器会在 3000 分支里递归调用自己。
 *   这个实例没有任何拦截器，返回的是网关原始响应 {code,message,data}。
 */
const autoLoginHttp = axios.create({
    baseURL: process.env.VUE_APP_BASE_URL,
    timeout: 30000,
});
/** 并发 3000 时共用同一次自动登录 */
let autoLoginPromise = null;
/** 最近一次自动登录成功时间，避免并发请求各自登录一遍 */
let lastAutoLoginSuccessAt = 0;

/**
 * 用登录页保存的当天凭据重新登录
 *
 * 1. 先取一个新的 checkCode 作为 AES 的 IV（与登录页同一套加密方式）
 * 2. 明文密码重新加密后调用「无验证码登录」接口
 * 3. 登录成功后写回 session，并清掉旧的 grantedToken
 *
 * @returns {Promise<Object|null>} 登录成功返回用户信息，失败返回 null
 */
async function doAutoLogin() {
    const credential = util.getAutoLoginCredential();
    if (!credential) {
        return null;
    }
    try {
        const checkResp = await autoLoginHttp.get("/basedata/base/getCheckNumber2");
        const check = checkResp && checkResp.data;
        if (!check || check.code !== "2000" || !check.data || !check.data.dictCode) {
            return null;
        }
        const iv = check.data.dictCode;
        const loginResp = await autoLoginHttp.post("/basedata/base/loginEmployeeByPasswordWithoutCode", {
            account: credential.account,
            // 与登录页一致：AES-CBC，checkCode 作为 iv
            password: AesUtil.encode(iv, credential.password),
            checkCode: iv,
        });
        const envelope = loginResp && loginResp.data;
        if (!envelope || envelope.code !== "2000" || !envelope.data) {
            // 账号密码已失效（改密/离职），清掉凭据，避免每次过期都重复尝试
            util.clearAutoLoginCredential();
            return null;
        }
        // grantedToken 的优先级高于 account.token，过期后必须清掉，否则重试仍会 3000
        sessionStorage.removeItem("grantedToken");
        util.setUserInfo(envelope.data);
        lastAutoLoginSuccessAt = Date.now();
        return envelope.data;
    } catch (e) {
        // 网络异常时保留凭据，下次过期再试
        return null;
    }
}

function ensureAutoLogin() {
    if (!autoLoginPromise) {
        autoLoginPromise = doAutoLogin().then(user => {
            autoLoginPromise = null;
            return user;
        }, () => {
            autoLoginPromise = null;
            return null;
        });
    }
    return autoLoginPromise;
}

/**
 * 自动登录成功后重试原请求
 *
 * ★ 必须新建 config：原 config 上的 CancelToken 已经被上一次请求用掉，
 *   而且 headers 里还带着过期 token。去掉这些头，让请求拦截器按新登录态重写。
 * ★ _autoLoginRetried 标记保证每个请求最多只自动登录并重试一次。
 */
function retryAfterAutoLogin(config) {
    const headers = {...(config.headers || {})};
    delete headers.token;
    delete headers.grantedToken;
    delete headers.systemCode;
    delete headers.merchantUuid;
    return axios({
        method: config.method,
        baseURL: config.baseURL,
        url: config.url,
        params: config.params,
        data: config.data,
        responseType: config.responseType,
        headers,
        _autoLoginRetried: true,
    });
}

// axios响应拦截器
// 声明成 async：企业微信登录分支要先查一次授权信息才能拼出跳转地址
axios.interceptors.response.use(
    async res => {
        stopProgress();
        // 过滤的白名单
        if (res.config.url.indexOf('/api') !== -1) {
            return Promise.resolve(res);
        } else if (!res.config.url.startsWith(process.env.VUE_APP_BASE_URL)) {
            // 访问其它网站
            return Promise.resolve(res);
        } else {
            // 如果是导出
            if (res.config.responseType === "blob") {
                return Promise.resolve(res.data);
            }
            if (res.data.code === '2000') {
                // 有请求成功说明登录态是好的，复位跳转闸门（见 loginRedirecting 说明）
                loginRedirecting = false;
                let uri = res.config.url.split("/");
                let lastPath = uri[uri.length - 1];
                // 操作成功
                if (lastPath.endsWith("save") || lastPath.endsWith("Save") || lastPath.startsWith("save")) {
                    // 是否提示
                    let isAlert = false;
                    // 短时间两个保存操作,只进行一次提示
                    let saveSuccess = util.getSession("saveSuccess");
                    if(!saveSuccess){
                        util.setSession("saveSuccess", 1 + ";" + new Date().getTime());
                        isAlert = true;
                    }else{
                        isAlert = false;
                        if (new Date().getTime() - saveSuccess.split(";")[1] > 1000) {
                            util.setSession("saveSuccess", 1 + ";" + new Date().getTime());
                            isAlert = true;
                        }
                    }
                    if(isAlert){
                        ElMessage.success({
                            message: '保存成功',
                            type: 'success',
                            duration: 2000,
                        });
                    }
                }
                if (lastPath.endsWith("delete") || lastPath.endsWith("Delete") || lastPath.startsWith("delete")) {
                    ElMessage.success({
                        message: '删除成功',
                        type: 'success',
                        duration: 2000,
                    });
                }
                return Promise.resolve(res.data);
            }
            if (!res || res.data.code !== '2000') {
                // 3000 的自动登录与兜底跳转共用
                const context = util.getContextPath();
                if (res.data.code === '3000') {
                    // 网页端：当天在登录页保存过凭据时，先自动登录并重试原请求（最多重试一次）
                    if (context === 1
                            && util.getAutoLoginCredential()
                            && !res.config._autoLoginRetried
                            && String(res.config.url || "").indexOf("/base/login") === -1) {
                        // 10 秒内刚自动登录过（并发请求陆续返回 3000），直接用新 token 重试，不再登录
                        const user = Date.now() - lastAutoLoginSuccessAt < 10000
                            ? {}
                            : await ensureAutoLogin();
                        if (user) {
                            loginRedirecting = false;
                            return retryAfterAutoLogin(res.config);
                        }
                    }
                }
                cancel && cancel()
                if (res.data.code === '3000') {
                    // 并发多个请求时 3000 会一起来，只处理第一个，其余直接丢弃
                    if (loginRedirecting) {
                        return;
                    }
                    loginRedirecting = true;
                    // 未登录或已过期，重新登录（context 与自动登录分支共用）
                    // ★ 商户id 必须在 clearSession 之前取：那是 sessionStorage.clear()，
                    // 取晚了就被一起清掉，下面也就无从判断该走哪套登录
                    const qywxMerchantId = util.getQywxMerchantId();
                    // 清空登录信息
                    util.clearSession();
                    if (context === 2 || context === 3) {
                        if (qywxMerchantId) {
                            // 从企业微信消息进来的：记住当前页，走该商户自建应用的授权登录。
                            // 记住地址要在 clearSession 之后写，否则会被上面那句清掉
                            util.setQywxBackUrl(window.location.pathname + window.location.search);
                            try {
                                await redirectToQywxAuth(qywxMerchantId);
                            } catch (e) {
                                // 查不到授权信息（未对接/网络异常）不该把人卡死，退回手动登录。
                                // 这里是 SPA 跳转、不会刷新页面，闸门要手动复位，
                                // 否则它一直为真，后续的 3000 会被静默吞掉、页面像死了一样
                                loginRedirecting = false;
                                router.push("/login");
                            }
                        } else {
                            let loginPage = window.location.origin + "/qywx/login";
                            let query = window.location.href.replace(window.location.origin, "");
                            // 企业微信打开
                            window.location = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${process.env.VUE_APP_QYWX_ID}&redirect_uri=${encodeURIComponent(loginPage)}&response_type=code&scope=snsapi_privateinfo&state=${query}#wechat_redirect`;
                        }
                    } else{
                        // web打开。SPA 跳转不刷新页面，闸门手动复位（见 loginRedirecting 说明）
                        loginRedirecting = false;
                        router.push("/login");
                    }
                    return;
                } else if (res.data.code === "1110") {
                    // 这是叙简登录返回的数据，不处理
                    return Promise.reject(res.data);
                }
                ElMessage.error({
                    message: res.data.message,  //'请求异常:' +
                    type: 'warning',
                    duration: 2000,
                });

                return Promise.reject(res.data);
            }
        }
    },
    error => {
        stopProgress();
        cancel && cancel()
        ElMessage.warning({
            message: '请求异常:' + JSON.stringify(error.message),
            type: 'warning',
            duration: 2000,
        });
        return Promise.reject(error);
    }
);

/**
 * 二进制响应（下载文件）。
 *
 * 响应拦截器对 responseType=blob 已经把 res.data（Blob）直接返回，
 * 包装层不能再取一次 .data，否则会得到 undefined。
 */
function isBinaryResponse(res) {
    return (typeof Blob !== "undefined" && res instanceof Blob)
        || (typeof ArrayBuffer !== "undefined" && res instanceof ArrayBuffer);
}

export default {
    get({url, data, baseUrl, responseType}, callBack=null) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'GET',
                url: url.startsWith("http") || url.startsWith("/api") ? url: (baseUrl? baseUrl + url : process.env.VUE_APP_BASE_URL + url),
                params: data,
                responseType: responseType,
            }).then(res => {
                if (res) resolve(isBinaryResponse(res) ? res : res.data)
                if (callBack) callBack(res); // 如果有回调就回调
            }).catch(err => {
                reject(err)
            })
        })
    },
    post({url, data, baseUrl}, callBack=null) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: url.startsWith("http") ? url: (baseUrl? baseUrl + url : process.env.VUE_APP_BASE_URL + url),
                data: data,
            }).then(res => {
                if (res) resolve(isBinaryResponse(res) ? res : res.data)
                if (callBack) callBack(res); // 如果有回调就回调
            }).catch(err => {
                reject(err)
            });
        })
    },
    del({url, data, baseUrl}, callBack=null) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'DELETE',
                url: url.startsWith("http") ? url: (baseUrl? baseUrl + url : process.env.VUE_APP_BASE_URL + url),
                params: data,
            }).then(res => {
                if (res) resolve(res.data)
                if (callBack) callBack(res); // 如果有回调就回调
            }).catch(err => {
                reject(err)
            })
        })
    }
};